import React from 'react';
import { Col, Row } from 'react-bootstrap';
import _ from 'lodash';
import { BooleanValueTemplate } from './valuesTemplates';
import { DocumentButtonTemplate, DocumentsButtonOptions, ActionButtonTemplate } from './buttonsTemplates';
import { ShowOnMapButtonTemplate } from './buttonsTemplates/ShowOnMapButtonTemplate';
import { Skeleton } from 'primereact/skeleton';
import { renderWithHtmlSubSup } from '../../shared/renderers/renderWithHtmlSubSup';

// Определяем, что может быть в колонке: поле или кнопка
export type ColumnField = {
  format: 'field';
  caption: string;
  field: string;
  valueType?: string;
};

export type ColumnButton = {
  format: 'button';
  type: 'documents' | 'showOnMap' | 'action';
  caption: string;
  actionKey?: string;
};

export type ColumnItem = ColumnField | ColumnButton;

export type ButtonOptions = {
    disabled?: boolean, 
    isLoading?: boolean,
}

export type ShowOnMapButtonOptions = ButtonOptions & { onClick?: () => void };

export type ActionButtonOptions = { disabled?: boolean; isLoading?: boolean; onClick?: () => void };

export type ButtonsOptions = {
  documents?: DocumentsButtonOptions;
  showOnMap?: ShowOnMapButtonOptions;
  customActions?: Record<string, ActionButtonOptions>;
};

export type CardInfoSectionProps = {
  sectionOptions: {
    sectionName: string;
    columns: ColumnItem[][];
  };
  data: Record<string, any> | null;
  buttonsOptions?: ButtonsOptions | null;
  isLoadingMainData: boolean
};

export function CardInfoSection({
  sectionOptions: { sectionName, columns },
  data,
  buttonsOptions,
  isLoadingMainData
}: CardInfoSectionProps) {
  
  // Разделяем элементы на поля и кнопки, чтобы кнопки рендерить в конце секции
  const buttonItems: ColumnButton[] = [];
  const fieldsColumns: ColumnItem[][] = columns.map((col) =>
    col.filter((item) => {
      if (item.format === 'button') {
        buttonItems.push(item as ColumnButton);
        return false;
      }
      return true;
    })
  );
  // Выделим элементы-изображения отдельно, чтобы рендерить их шириной в две колонки
  const imageItems: { caption: string; value: any; isLoading: boolean }[] = [];
  const filteredFieldsColumns: ColumnItem[][] = fieldsColumns.map((col) =>
    col.filter((item) => {
      if (item.format !== 'field') return true;
      const { field, caption } = item as any;
      let rawValue: any = _.get(data, field);
      let loading = (data as any)?.isLoadingMainData;
      if (typeof rawValue === 'object' && rawValue !== null) {
        loading = rawValue.isLoading;
        rawValue = rawValue.value;
      }
      const isImage = typeof rawValue === 'object' && rawValue?.type === 'image';
      if (isImage) {
        imageItems.push({ caption, value: rawValue, isLoading: !!loading });
        return false;
      }
      return true;
    })
  );
  return (
    <div className="card-info-section-div">
      <p className="section-name">{sectionName}</p>

      {imageItems.length > 0 && (
        <Row>
          {imageItems.map((img, idx) => (
            <Col xs={12} sm={12} md={12} lg={6} key={`img-${idx}`}>
              <div className="field-block">
                {!img.value?.hideCaption && <div className="field-caption">{img.caption}</div>}
                {img.isLoading ? (
                  <Skeleton className="field-value-skeleton" />
                ) : (
                  <div className="field-value">
                    <img src={img.value.url} alt={img.value.alt ?? ''} style={{ height: 'auto', maxHeight: 300 }} />
                  </div>
                )}
              </div>
            </Col>
          ))}
        </Row>
      )}

      <Row>
        {filteredFieldsColumns.map((colItems, colIndex) => (
          <Col xs={12} sm={12} md={6} lg={3} key={colIndex}>
            {colItems.map((item, idx) => {
              if (item.format === 'field') {
                const { caption, field } = item;
                let fieldValue: any = _.get(data, field);
                let loading = isLoadingMainData;

                if (typeof fieldValue === 'object' && fieldValue !== null) {
                  loading = fieldValue.isLoading;
                  fieldValue = fieldValue.value;
                }

                return (
                  <div className="field-block" key={idx}>
                    <div className="field-caption">{renderWithHtmlSubSup(caption)}</div>
                    {loading ? (
                      <Skeleton className="field-value-skeleton" />
                    ) : typeof fieldValue === 'boolean' ? (
                      <BooleanValueTemplate value={fieldValue} />
                    ) : (typeof fieldValue === 'object' && fieldValue?.type === 'image') ? (
                      <div className="field-value">
                        <img src={fieldValue.url} alt={fieldValue.alt ?? ''} style={{ height: 'auto', maxHeight: 300 }} />
                      </div>
                    ) : (
                      <div className="field-value" style={{ whiteSpace: 'pre-wrap' }}>
                        {typeof fieldValue === 'string' ? renderWithHtmlSubSup(fieldValue) : (fieldValue ?? 'Не указано')}
                      </div>
                    )}
                  </div>
                );
              }

              return null;
            })}
          </Col>
        ))}
      </Row>

      {buttonItems.length > 0 && (
        <Row>
          <Col xs={12}>
            <div className="buttons-row">
            {buttonItems.map((btn, idx) => {
              if (btn.type === 'documents') {
                const opts = buttonsOptions?.documents ?? {
                  numberOfDocuments: 0,
                  disabled: true,
                  isLoading: false,
                };
                return (
                  <div className="button-block" key={`doc-${idx}`}>
                    <DocumentButtonTemplate caption={btn.caption} buttonOptions={opts} />
                  </div>
                );
              }
              if (btn.type === 'showOnMap') {
                const opts = buttonsOptions?.showOnMap ?? { disabled: false, isLoading: false };
                return (
                  <div className="button-block" key={`map-${idx}`}>
                    <ShowOnMapButtonTemplate caption={btn.caption} buttonOptions={opts} />
                  </div>
                );
              }
              if (btn.type === 'action') {
                const key = btn.actionKey ?? '';
                const opts = buttonsOptions?.customActions?.[key] ?? { disabled: true };
                return (
                  <div className="button-block" key={`action-${idx}`}>
                    <ActionButtonTemplate caption={btn.caption} buttonOptions={opts} />
                  </div>
                );
              }
              return null;
            })}
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
}
